import React from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { AgentVersionConfigurationInput } from "src/agents/types";
import { AgentModelInfo, Model } from "src/clients/agent-management";
import { ModelOption, ModelPicker } from "src/genai/ModelSelector";
import {
  hostingForModel,
  lifecycleFor,
  regionFromRouting,
  successorIdFor,
} from "src/genai/ModelSelector/facets";
import {
  withByokPricing,
  withTpmQuota,
} from "src/genai/ModelSelector/modelOptions";

type AgentModelSelectorProps = {
  /** Whether the selector is disabled (e.g., for published versions) */
  disabled?: boolean;
  /** Available models adapted for the picker */
  modelOptions: ModelOption[];
};

/**
 * Build the compound model option ID used to highlight the selected model.
 * BYOK entries use "model_id:byok:credential_id", Taktile defaults use plain model_id.
 */
const buildSelectedOptionId = (
  model: string,
  credentialId: string | null | undefined,
): string => {
  if (credentialId) {
    return `${model}:byok:${credentialId}`;
  }
  return model;
};

/**
 * Parse a compound model option ID back into model and credential_id.
 * "gpt-5:byok:uuid" → { model: "gpt-5", credentialId: "uuid" }
 * "gpt-5" → { model: "gpt-5", credentialId: null }
 */
const parseSelectedOptionId = (
  optionId: string,
): { model: string; credentialId: string | null } => {
  const byokMarker = ":byok:";
  const idx = optionId.indexOf(byokMarker);
  if (idx !== -1) {
    return {
      model: optionId.slice(0, idx),
      credentialId: optionId.slice(idx + byokMarker.length),
    };
  }
  return { model: optionId, credentialId: null };
};

/**
 * Model selector component for agents.
 * Models are passed down from the parent component which fetches them.
 */
export const AgentModelSelector: React.FC<AgentModelSelectorProps> = ({
  disabled = false,
  modelOptions,
}) => {
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  // Subscribe to credential_id changes so the component re-renders when
  // switching between shared-key and BYOK variants of the same model.
  // Using getValues() would only read the value once and miss updates
  // when the model name stays the same but the credential changes.
  const credentialId = useWatch({
    control,
    name: "model_config.credential_id",
  });

  return (
    <Controller
      control={control}
      name="model_config.model"
      render={({ field }) => {
        const selectedId = buildSelectedOptionId(field.value, credentialId);
        // A non-null credential_id with no matching option means the BYOK
        // credential was deleted — the raw compound id is useless, so surface a
        // friendly label that still names the underlying model when we can
        // resolve a display name (from a shared or sibling BYOK option).
        const isDeletedBYOKCredential =
          credentialId != null &&
          !modelOptions.some((m) => m.id === selectedId);
        const modelDisplayName =
          modelOptions.find(
            (m) =>
              m.id === field.value || m.id.startsWith(`${field.value}:byok:`),
          )?.displayName ?? field.value;

        return (
          <ModelPicker
            dataLoc="agent-model-selector"
            isReadOnly={disabled}
            missingModelDisplayName={
              isDeletedBYOKCredential
                ? `${modelDisplayName} using a deleted custom AI credential`
                : undefined
            }
            models={modelOptions}
            selectedModelId={selectedId}
            onSelect={(optionId) => {
              const parsed = parseSelectedOptionId(optionId);
              field.onChange(parsed.model as Model);
              setValue("model_config.credential_id", parsed.credentialId, {
                shouldDirty: true,
              });
            }}
          />
        );
      }}
    />
  );
};

export const convertAgentModelInfoToModelOption = (
  model: AgentModelInfo,
): ModelOption => ({
  id: buildSelectedOptionId(model.model_id, model.credential_id),
  displayName: model.display_name,
  provider: model.provider,
  lifecycle: lifecycleFor(model),
  hosting: hostingForModel(model),
  region: regionFromRouting(model.routing_target_region),
  byok: Boolean(model.credential_id),
  deploymentLabel: model.credential_id
    ? (model.credential_name ?? undefined)
    : undefined,
  successorId: successorIdFor(model),
  intelligence: model.intelligence,
  speed: model.speed,
  inputCostPer1MTokens: model.input_cost_per_1m_tokens ?? null,
  outputCostPer1MTokens: model.output_cost_per_1m_tokens ?? null,
  contextWindowTokens: model.context_window_size,
  releaseDate: model.release_date,
  // Merged from the workspace LLM quota by withTpmQuota in agentModelsToOptions.
  tokensPerMinuteQuota: null,
});

/**
 * Adapt the agent model list into {@link ModelOption}s: backfill BYOK pricing
 * from the shared sibling and merge the per-model TPM quota. Agents share the
 * workspace LLM quota with aiNode (`tpmByModelId` keyed by `model_id`).
 */
export const agentModelsToOptions = (
  models: AgentModelInfo[],
  tpmByModelId: Record<string, number> = {},
): ModelOption[] =>
  withByokPricing(
    withTpmQuota(models.map(convertAgentModelInfoToModelOption), tpmByModelId),
  );
